import type { EmailOtpType, Session, SupabaseClient } from '@supabase/supabase-js'
import {
  parseAuthCallbackFromLocation,
  resolveAuthCallbackDestination,
} from '@/lib/auth/authCallbackFromUrl'
import { markRecoverySessionPending } from '@/lib/auth/recoverySession'

function markRecoveryIfNeeded(destination: string, type: string | null): void {
  if (destination === '/reset-password' || type?.toLowerCase() === 'recovery') {
    markRecoverySessionPending()
  }
}

export type ConsumeAuthCallbackResult =
  | { status: 'none' }
  | {
      status: 'consumed'
      destination: string
      session: Session
      type: string | null
    }
  | { status: 'error'; error: unknown }

/**
 * Establishes a browser session from a Supabase email redirect URL.
 *
 * Handles:
 * - Implicit fragments (`#access_token=…&type=recovery`) — never visible to
 *   server routes; must be consumed in the browser
 * - PKCE query codes (`?code=…`)
 * - OTP token_hash links (`?token_hash=…&type=recovery`)
 *
 * Callers own navigation: hard-nav to `destination` (hash/email flows). Do not
 * stash destinations in sessionStorage — that leaked into later password logins.
 */
export async function consumeAuthCallbackFromUrl(
  supabase: SupabaseClient,
  location: { hash: string; search: string; pathname: string }
): Promise<ConsumeAuthCallbackResult> {
  const parsed = parseAuthCallbackFromLocation(location)
  if (!parsed) return { status: 'none' }

  if (parsed.kind === 'implicit') {
    const { data, error } = await supabase.auth.setSession({
      access_token: parsed.access_token,
      refresh_token: parsed.refresh_token,
    })

    if (error || !data.session?.user) {
      return { status: 'error', error: error ?? new Error('setSession returned no session') }
    }

    const destination = resolveAuthCallbackDestination({
      type: parsed.type,
      pathname: location.pathname,
    })
    markRecoveryIfNeeded(destination, parsed.type)

    return {
      status: 'consumed',
      destination,
      session: data.session,
      type: parsed.type,
    }
  }

  if (parsed.kind === 'otp') {
    const { data, error } = await supabase.auth.verifyOtp({
      type: parsed.type as EmailOtpType,
      token_hash: parsed.token_hash,
    })

    if (error || !data.session?.user) {
      return { status: 'error', error: error ?? new Error('verifyOtp returned no session') }
    }

    const destination = resolveAuthCallbackDestination({
      type: parsed.type,
      next: parsed.next,
      pathname: location.pathname,
    })
    markRecoveryIfNeeded(destination, parsed.type)

    return {
      status: 'consumed',
      destination,
      session: data.session,
      type: parsed.type,
    }
  }

  const { data, error } = await supabase.auth.exchangeCodeForSession(parsed.code)
  if (error || !data.session?.user) {
    return {
      status: 'error',
      error: error ?? new Error('exchangeCodeForSession returned no session'),
    }
  }

  const destination = resolveAuthCallbackDestination({
    next: parsed.next,
    type: parsed.type,
    pathname: location.pathname,
  })
  markRecoveryIfNeeded(destination, parsed.type)

  return {
    status: 'consumed',
    destination,
    session: data.session,
    type: parsed.type,
  }
}

/** Strip auth tokens from the address bar after a successful consume. */
export function clearAuthCallbackFromAddressBar(): void {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  url.hash = ''
  url.searchParams.delete('code')
  url.searchParams.delete('token_hash')
  url.searchParams.delete('type')
  url.searchParams.delete('next')
  url.searchParams.delete('access_token')
  url.searchParams.delete('refresh_token')
  url.searchParams.delete('expires_in')
  url.searchParams.delete('expires_at')
  url.searchParams.delete('token_type')
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`)
}
